import {
  TableContainer,
  Paper,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Box,
} from '@mui/material';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import HighlightSyntax from './HighlightSyntax';
import { useEffect, useRef } from 'react';
import FormatLink from './FormatLink';
import { formatNumberSequence } from '@buildwise/libs/helpers';

type RenderLLMMarkdownProps = {
  llmResponse: string;
  getSourceRefId?: (index: number) => string;
};

function RenderLLMMarkdown({ llmResponse, getSourceRefId }: RenderLLMMarkdownProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      const allCitationElements = containerRef.current.querySelectorAll('.citation');
      const formattedOrder = formatNumberSequence(
        Array.from(allCitationElements).map(
          (element) => Number(element.textContent?.replaceAll('[', '').replaceAll(']', '')) || 0,
        ),
      );
      allCitationElements.forEach((element, index) => {
        element.textContent = `[${formattedOrder[index].orderValue + 1}]`;
        (element as HTMLAnchorElement).style.visibility = 'visible';
      });
    }
  }, [containerRef.current]);

  return (
    <Box ref={containerRef}>
      <Markdown
        className="line-break"
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          table: (props) => (
            <TableContainer
              component={Paper}
              sx={(theme) => ({
                boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
              })}
            >
              <Table>{props.children}</Table>
            </TableContainer>
          ),
          th: (props) => <TableCell>{props.children}</TableCell>,
          thead: (props) => <TableHead>{props.children}</TableHead>,
          tr: (props) => <TableRow>{props.children}</TableRow>,
          tbody: (props) => <TableBody>{props.children}</TableBody>,
          td: (props) => <TableCell>{props.children}</TableCell>,
          a: (props) => <FormatLink {...props} getSourceRefId={getSourceRefId} />,
          code: HighlightSyntax,
        }}
      >
        {llmResponse}
      </Markdown>
    </Box>
  );
}

export default RenderLLMMarkdown;
